import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const STATUS_STYLES = {
  open:        { bg: 'rgba(251,191,36,0.15)', color: '#fbbf24', label: 'Open' },
  in_progress: { bg: 'rgba(96,165,250,0.15)', color: '#60a5fa', label: 'In Progress' },
  resolved:    { bg: 'rgba(74,222,128,0.15)', color: '#4ade80', label: 'Resolved' },
};

const fmtDate = ts => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtDateTime = ts => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { bg: 'var(--bg-elevated)', color: 'var(--text-muted)', label: status };
  return (
    <span style={{ background: s.bg, color: s.color, padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
}

function TicketModal({ ticket, onClose, onUpdated }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(ticket);

  const fetchDetail = useCallback(async () => {
    try {
      const r = await axios.get(`/api/support/tickets/${ticket.id}`);
      setMessages(r.data.messages);
      setCurrentTicket(r.data.ticket);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [ticket.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`/api/support/tickets/${ticket.id}/messages`, { message: reply });
      setReply('');
      await fetchDetail();
      onUpdated();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleStatus = async status => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`/api/support/tickets/${ticket.id}/status`, { status });
      await fetchDetail();
      onUpdated();
    } catch (err) { console.error(err); }
    finally { setUpdatingStatus(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: '600px', display: 'flex', flexDirection: 'column', maxHeight: '85vh' }}>
        <div className="modal-header" style={{ gap: '12px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="modal-title" style={{ fontSize: '1.1rem', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentTicket.subject}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <StatusBadge status={currentTicket.status} />
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                {currentTicket.first_name} {currentTicket.last_name}
                <span style={{ textTransform: 'capitalize', marginLeft: '4px', color: 'var(--text-muted)' }}>({currentTicket.user_role})</span>
              </span>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{fmtDate(currentTicket.created_at)}</span>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Thread */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {loading ? (
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading...</p>
          ) : messages.map(msg => {
            const isAdmin = msg.role === 'admin';
            return (
              <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isAdmin ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '80%',
                  background: isAdmin ? 'rgba(96,165,250,0.12)' : 'var(--bg-elevated)',
                  border: `1px solid ${isAdmin ? 'rgba(96,165,250,0.25)' : 'var(--border)'}`,
                  borderRadius: isAdmin ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  padding: '10px 14px',
                }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: '600', color: isAdmin ? '#60a5fa' : 'var(--text-primary)', marginBottom: '4px' }}>
                    {isAdmin ? 'Support Team' : `${msg.first_name} ${msg.last_name}`}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {msg.message}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '6px', textAlign: isAdmin ? 'right' : 'left' }}>
                    {fmtDateTime(msg.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Status controls */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status:</span>
          {['open', 'in_progress', 'resolved'].map(s => (
            <button key={s} onClick={() => handleStatus(s)} disabled={updatingStatus || currentTicket.status === s}
              style={{
                padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer',
                border: currentTicket.status === s ? `1px solid ${STATUS_STYLES[s].color}` : '1px solid var(--border)',
                background: currentTicket.status === s ? STATUS_STYLES[s].bg : 'transparent',
                color: currentTicket.status === s ? STATUS_STYLES[s].color : 'var(--text-muted)',
                opacity: updatingStatus ? 0.6 : 1,
              }}>
              {STATUS_STYLES[s].label}
            </button>
          ))}
        </div>

        {/* Reply box */}
        {currentTicket.status !== 'resolved' && (
          <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <textarea
              className="form-input"
              rows={3}
              placeholder="Write a reply..."
              value={reply}
              onChange={e => setReply(e.target.value)}
              style={{ resize: 'vertical', fontSize: '0.875rem' }}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="btn btn-sm" onClick={handleSend} disabled={sending || !reply.trim()}
                style={{ opacity: (!reply.trim() || sending) ? 0.5 : 1 }}>
                {sending ? 'Sending...' : 'Send Reply'}
              </button>
            </div>
          </div>
        )}
        {currentTicket.status === 'resolved' && (
          <div style={{ padding: '12px 24px 16px', borderTop: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            This ticket is resolved. Change the status to reply.
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminSupport({ onNotifRead }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      const params = filter !== 'all' ? { status: filter } : {};
      const r = await axios.get('/api/support/tickets', { params });
      setTickets(r.data.tickets);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    fetchTickets();
    // Mark all support notifications as read when viewing support
    axios.patch('/api/support/notifications/read').catch(() => {});
    if (onNotifRead) onNotifRead();
  }, [fetchTickets, onNotifRead]);

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'resolved', label: 'Resolved' },
  ];

  const openCount = tickets.filter(t => t.status === 'open').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">SUPPORT</h1>
        <p className="page-subtitle">Manage support tickets from parents and instructors.</p>
      </div>

      {/* Summary */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <div className="stat-card-label">Open</div>
          <div className="stat-card-value" style={{ color: '#fbbf24' }}>{tickets.filter(t => t.status === 'open').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">In Progress</div>
          <div className="stat-card-value" style={{ color: '#60a5fa' }}>{tickets.filter(t => t.status === 'in_progress').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Resolved</div>
          <div className="stat-card-value" style={{ color: '#4ade80' }}>{tickets.filter(t => t.status === 'resolved').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Total</div>
          <div className="stat-card-value">{tickets.length}</div>
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: 'var(--radius-md)', width: 'fit-content', border: '1px solid var(--border)' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            style={{
              padding: '6px 16px', borderRadius: 'calc(var(--radius-md) - 2px)', fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', border: 'none',
              background: filter === f.key ? 'var(--bg-card)' : 'transparent',
              color: filter === f.key ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: filter === f.key ? '0 1px 3px rgba(0,0,0,0.3)' : 'none',
              transition: 'all 0.15s',
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="empty-state"><p>Loading...</p></div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🎫</div>
            <p className="empty-state-text">No {filter !== 'all' ? filter.replace('_', ' ') : ''} tickets</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>From</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Replies</th>
                <th>Last Activity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedTicket(t)}>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.8rem', width: '48px' }}>#{t.id}</td>
                  <td>
                    <div className="td-primary">{t.first_name} {t.last_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{t.user_role}</div>
                  </td>
                  <td className="td-primary" style={{ maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td style={{ textAlign: 'center' }}>{t.message_count}</td>
                  <td style={{ fontSize: '0.8rem' }}>{fmtDateTime(t.last_message_at || t.updated_at)}</td>
                  <td>
                    <button className="btn btn-outline btn-sm" onClick={e => { e.stopPropagation(); setSelectedTicket(t); }}>
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedTicket && (
        <TicketModal
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
          onUpdated={() => { setSelectedTicket(null); fetchTickets(); }}
        />
      )}
    </div>
  );
}
